import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ScreenRotationIcon from '@mui/icons-material/ScreenRotation';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { GATE_POSITIONS, WEST_PROPORTION, EAST_PROPORTION, TUNNELS } from '../flight-data/gatePositions';
import { COLORS } from './styles';

// Internal logical dimensions of the landscape canvas. Wider than the device
// viewport on purpose — gates need room to breathe at the same proportional
// spacing as the main app's ConcourseMap. We auto-fit-zoom to the viewport on
// mount and on orientation change.
const MAP_W = 900;
const MAP_H = 200;
const GATE_SIZE = 24;
const BADGE_SIZE = 16;

// Outer viewport heights. The map is bigger than this; transform: scale()
// fits it on first render.
const VIEWPORT_H_LANDSCAPE = 180;
const VIEWPORT_H_PORTRAIT = 360;

// Vertical layout (mirrors ConcourseMap.js):
//   North row top: 40
//   Top concourse line: 70
//   Bottom concourse line: 130
//   South row top: 135
const NORTH_ROW_TOP = 40;
const CONCOURSE_LINE_TOP = 70;
const CONCOURSE_LINE_BOTTOM = 130;
const SOUTH_ROW_TOP = 135;
const TUNNEL_HEIGHT = CONCOURSE_LINE_BOTTOM - CONCOURSE_LINE_TOP; // 60

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.15;

function MiniConcourseMap({ driver, inboundGate, defaultOrientation = 'portrait' }) {
  const [orientation, setOrientation] = useState(defaultOrientation);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const viewportRef = useRef(null);

  const isPortrait = orientation === 'portrait';

  // Bounding box of the rotated canvas (what gets centered + zoomed).
  const wrapperW = isPortrait ? MAP_H : MAP_W;
  const wrapperH = isPortrait ? MAP_W : MAP_H;
  const viewportH = isPortrait ? VIEWPORT_H_PORTRAIT : VIEWPORT_H_LANDSCAPE;

  // Auto-fit zoom on mount and on orientation change so the whole map is
  // visible inside the viewport by default. User can zoom in/out from there.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return;
    const fit = Math.min(r.width / wrapperW, r.height / wrapperH);
    setZoom(Math.max(MIN_ZOOM, Math.min(1, +fit.toFixed(2))));
    setPan({ x: 0, y: 0 });
  }, [orientation, wrapperW, wrapperH]);

  const flights = driver?.flights || [];
  const inboundGateNum = parseInt(String(inboundGate || '').replace(/\D/g, ''), 10);

  const routeSteps = flights.map((f, i) => {
    if (f.connectionCategory === 'cold') return { type: 'cold_sort', stopOrder: i + 1, flight: f };
    if (f.connectionCategory === 'cancelled') return { type: 'reroutes', stopOrder: i + 1, flight: f };
    const gn = parseInt(String(f.gate || '').replace(/\D/g, ''), 10);
    return { type: 'gate', gateNum: gn, stopOrder: i + 1, flight: f };
  });

  const gateStops = {};
  routeSteps.forEach((s) => {
    if (s.type !== 'gate' || isNaN(s.gateNum)) return;
    if (!gateStops[s.gateNum]) gateStops[s.gateNum] = [];
    gateStops[s.gateNum].push(s);
  });

  const usesColdSort = routeSteps.some(s => s.type === 'cold_sort');
  const usesReroutes = routeSteps.some(s => s.type === 'reroutes');

  const gatesByGroup = (wing, side) =>
    Object.keys(GATE_POSITIONS).map(Number).filter(
      g => GATE_POSITIONS[g].wing === wing && GATE_POSITIONS[g].side === side
    );

  // ── Zoom & pan handlers ──────────────────────────────────────────────
  const zoomIn = () => setZoom(z => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom(z => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)));
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const onWheel = useCallback((e) => {
    if (!e.ctrlKey && !e.metaKey) return; // require modifier so it doesn't hijack page scroll
    e.preventDefault();
    setZoom(z => {
      const next = z + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
      return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, +next.toFixed(2)));
    });
  }, []);

  const onMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const startPan = { ...pan };
    const move = (e2) => {
      setPan({ x: startPan.x + (e2.clientX - startX), y: startPan.y + (e2.clientY - startY) });
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // Pinch zoom for touch devices
  const pinchRef = useRef({ initialDist: 0, initialZoom: 1 });
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const [a, b] = e.touches;
      pinchRef.current.initialDist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
      pinchRef.current.initialZoom = zoom;
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current.initialDist > 0) {
      const [a, b] = e.touches;
      const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
      const ratio = dist / pinchRef.current.initialDist;
      const next = pinchRef.current.initialZoom * ratio;
      setZoom(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, +next.toFixed(2))));
    }
  };

  // ── Gate rendering ──────────────────────────────────────────────────
  const renderGate = (gateNum) => {
    const pos = GATE_POSITIONS[gateNum];
    if (!pos) return null;
    const stops = gateStops[gateNum] || [];
    const isInbound = gateNum === inboundGateNum;
    const hasStops = stops.length > 0;

    let bg = '#1e1e1e';
    let textColor = '#666';
    if (isInbound) { bg = '#F9B612'; textColor = '#000'; }
    else if (hasStops) { bg = COLORS.accent; textColor = '#0a1020'; }

    return (
      <Box key={gateNum} sx={{ position: 'absolute', left: pos.left }}>
        <Box sx={{ position: 'relative' }}>
          {stops.map((s, i) => (
            <Box key={i} sx={{
              position: 'absolute',
              ...(pos.side === 'north' ? { top: -BADGE_SIZE / 2 - 4 } : { bottom: -BADGE_SIZE / 2 - 4 }),
              left: '50%',
              transform: `translateX(${i === 0 ? '-50%' : `calc(-50% + ${i * 12}px)`}) ${isPortrait ? 'rotate(-90deg)' : ''}`,
              width: BADGE_SIZE, height: BADGE_SIZE,
              borderRadius: '50%',
              backgroundColor: COLORS.accent,
              color: '#0a1020',
              fontSize: '0.65rem',
              fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              lineHeight: 1, zIndex: 2,
              border: '1px solid #0f172a',
            }}>
              {s.stopOrder}
            </Box>
          ))}
          <Box sx={{
            width: GATE_SIZE, height: GATE_SIZE,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: bg, color: textColor,
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: isInbound || hasStops ? 700 : 400,
            transition: 'transform 200ms',
            transform: `${hasStops ? 'scale(1.05)' : 'scale(1)'} ${isPortrait ? 'rotate(-90deg)' : ''}`,
          }}>
            {gateNum}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      borderRadius: 1.5,
      p: 1,
      pt: 0.5,
    }}>
      {/* Toolbar */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        mb: 0.5,
      }}>
        <Typography sx={{ fontSize: '0.55rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Concourse · {(zoom * 100).toFixed(0)}%
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <SmallBtn onClick={zoomOut} disabled={zoom <= MIN_ZOOM}><RemoveIcon sx={{ fontSize: 12 }} /></SmallBtn>
          <SmallBtn onClick={zoomIn}  disabled={zoom >= MAX_ZOOM}><AddIcon sx={{ fontSize: 12 }} /></SmallBtn>
          <SmallBtn onClick={resetView} disabled={zoom === 1 && pan.x === 0 && pan.y === 0}>
            <RestartAltIcon sx={{ fontSize: 12 }} />
          </SmallBtn>
          <SmallBtn onClick={() => { setOrientation(o => o === 'portrait' ? 'landscape' : 'portrait'); resetView(); }}>
            <ScreenRotationIcon sx={{ fontSize: 12 }} />
          </SmallBtn>
        </Box>
      </Box>

      {/* Map viewport (handles wheel/touch + clipping) */}
      <Box
        ref={viewportRef}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        sx={{
          width: '100%',
          height: viewportH,
          position: 'relative',
          overflow: 'hidden',
          touchAction: 'none',
          cursor: zoom > 0.5 ? 'grab' : 'default',
          '&:active': { cursor: zoom > 0.5 ? 'grabbing' : 'default' },
        }}
      >
        {/* Inner zoom/pan layer */}
        <Box sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: wrapperW,
          height: wrapperH,
          transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: 'center center',
          transition: 'transform 60ms linear',
        }}>
          {/* Rotation layer — landscape canvas, possibly rotated 90° */}
          <Box sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: MAP_W,
            height: MAP_H,
            transform: `translate(-50%, -50%) ${isPortrait ? 'rotate(90deg)' : ''}`,
            transformOrigin: 'center center',
          }}>
            {/* North row */}
            <Box sx={{ position: 'absolute', top: NORTH_ROW_TOP, left: 0, width: '100%', display: 'flex' }}>
              <Box sx={{ display: 'flex', width: `${WEST_PROPORTION}%`, paddingRight: '10px', position: 'relative' }}>
                {gatesByGroup('west', 'north').map(renderGate)}
              </Box>
              <Box sx={{ display: 'flex', width: `${EAST_PROPORTION}%`, paddingLeft: '10px', position: 'relative' }}>
                {gatesByGroup('east', 'north').map(renderGate)}
              </Box>
            </Box>

            {/* Concourse lines */}
            <Box sx={{ position: 'absolute', top: CONCOURSE_LINE_TOP, left: 0, width: `${WEST_PROPORTION - 1}%`, height: 3, backgroundColor: '#e2e2e2' }} />
            <Box sx={{ position: 'absolute', top: CONCOURSE_LINE_TOP, right: 0, width: `${EAST_PROPORTION - 1}%`, height: 3, backgroundColor: '#e2e2e2' }} />
            <Box sx={{ position: 'absolute', top: CONCOURSE_LINE_BOTTOM, left: 0, width: `${WEST_PROPORTION - 1}%`, height: 3, backgroundColor: '#e2e2e2' }} />
            <Box sx={{ position: 'absolute', top: CONCOURSE_LINE_BOTTOM, right: 0, width: `${EAST_PROPORTION - 1}%`, height: 3, backgroundColor: '#e2e2e2' }} />

            {/* South row */}
            <Box sx={{ position: 'absolute', top: SOUTH_ROW_TOP, left: 0, width: '100%', display: 'flex' }}>
              <Box sx={{ display: 'flex', width: `${WEST_PROPORTION}%`, paddingRight: '10px', position: 'relative' }}>
                {gatesByGroup('west', 'south').map(renderGate)}
              </Box>
              <Box sx={{ display: 'flex', width: `${EAST_PROPORTION}%`, paddingLeft: '10px', position: 'relative' }}>
                {gatesByGroup('east', 'south').map(renderGate)}
              </Box>
            </Box>

            {/* Tunnels + facilities */}
            {TUNNELS.map((tunnel, idx) => {
              let leftPos;
              if (tunnel.section === 'west') {
                const rel = (tunnel.position - 23) / (39 - 23);
                leftPos = `calc(${rel * WEST_PROPORTION}%)`;
              } else {
                const rel = (tunnel.position - 40) / (67 - 40);
                leftPos = `calc(${WEST_PROPORTION}% + ${rel * EAST_PROPORTION}%)`;
              }
              const isWestTunnel = tunnel.label === 'West Tunnel';
              const isEastTunnel = tunnel.label === 'East Tunnel';
              return (
                <Box key={`tun-${idx}`} sx={{ position: 'absolute', left: leftPos, top: CONCOURSE_LINE_TOP, transform: 'translateX(-7px)' }}>
                  <Box sx={{ width: tunnel.label === 'Breezeway' ? 14 : 18, height: TUNNEL_HEIGHT, backgroundColor: '#e2e2e2' }} />
                  {isWestTunnel && usesColdSort && (
                    <Box sx={{ position: 'absolute', top: '50%', left: 22, transform: `translateY(-50%) ${isPortrait ? 'rotate(-90deg)' : ''}` }}>
                      <FacilityChip label="Cold Sort" color="#42A5F5" />
                    </Box>
                  )}
                  {isEastTunnel && usesReroutes && (
                    <Box sx={{ position: 'absolute', top: '50%', right: 22, transform: `translateY(-50%) ${isPortrait ? 'rotate(-90deg)' : ''}` }}>
                      <FacilityChip label="Reroutes" color="#FF1744" />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25, mt: 0.5 }}>
        <LegendDot color="#F9B612" label="Inbound" />
        <LegendDot color={COLORS.accent} label="Stop" />
      </Box>
    </Box>
  );
}

function SmallBtn({ children, onClick, disabled }) {
  return (
    <IconButton
      onClick={onClick}
      disabled={disabled}
      size="small"
      sx={{
        width: 22, height: 22,
        backgroundColor: 'rgba(34, 211, 238, 0.1)',
        color: COLORS.accent,
        border: `1px solid ${COLORS.divider}`,
        borderRadius: 0.75,
        '&:hover': { backgroundColor: 'rgba(34, 211, 238, 0.25)' },
        '&.Mui-disabled': { color: COLORS.textMuted, opacity: 0.3 },
      }}
    >
      {children}
    </IconButton>
  );
}

function FacilityChip({ label, color }) {
  return (
    <Box sx={{
      backgroundColor: `${color}33`,
      color,
      px: 0.5, py: 0.25,
      borderRadius: 0.5,
      fontSize: '0.5rem',
      fontWeight: 700,
      whiteSpace: 'nowrap',
      letterSpacing: 0.3,
      border: `1px solid ${color}`,
    }}>
      {label}
    </Box>
  );
}

function LegendDot({ color, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color }} />
      <Typography sx={{ fontSize: '0.55rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Typography>
    </Box>
  );
}

export default MiniConcourseMap;
